import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, describeError } from "@/lib/api";
import { accountResults, readStore, stats } from "@/lib/mini";
import { takeReturnTo, remember, NEW_FROM_POLL_KEY } from "@/lib/returnTo";
import { toast } from "sonner";

export default function Register() {
  const navigate = useNavigate();
  const { user, register, upgradeGuest } = useAuth();
  // A guest signing up keeps their account, games and raider.
  const guest = !!user?.is_guest;
  const [form, setForm] = useState({ email: "", username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Mini puzzles played on this device before signing up (pages/Mini.jsx):
  // offer to carry the streak into the new account (Jack, 2026-10-26).
  const [mini] = useState(() => {
    const { results } = readStore();
    const nums = Object.keys(results).map(Number).filter((n) => results[n].finished);
    if (!nums.length) return null;
    const st = stats(results, Math.max(...nums));
    return { results, streak: st.streak, wins: st.wins };
  });
  const [keepMini, setKeepMini] = useState(true);
  const carryMini = async () => {
    if (!mini || !keepMini) return;
    const results = accountResults(mini.results);
    try { await api.post("/mini/mine", { results }); } catch { /* the device still has them */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (guest) {
        await upgradeGuest(form.email, form.username, form.password);
        await carryMini();
        toast.success("Account created. Your games came with you.");
        navigate(takeReturnTo() || "/lobby", { replace: true });
        return;
      }
      await register(form.email, form.username, form.password);
      await carryMini();
      toast.success("Welcome to Jangle!");
      // Signed up from the name poll link: vote first, then the usual steps.
      const back = takeReturnTo();
      if (back) remember(NEW_FROM_POLL_KEY, "1");
      navigate(back || "/avatar?welcome=1", { replace: true });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md" data-testid="register-page">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl font-black tracking-tighter">Create your account</h1>
          <p className="text-muted-foreground mt-2">
            {guest ? "Keep your games and raider, and play people for Elo." : "Free forever. Starting Elo: 1200."}
          </p>
        </div>

        <form onSubmit={submit} className="bg-surface border border-border rounded-2xl p-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Username
            </Label>
            <Input
              id="username"
              value={form.username}
              onChange={set("username")}
              required
              minLength={2}
              maxLength={24}
              className="bg-background h-11"
              data-testid="register-username-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={set("email")}
              required
              className="bg-background h-11"
              data-testid="register-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={set("password")}
              required
              minLength={6}
              className="bg-background h-11"
              data-testid="register-password-input"
            />
            <p className="text-xs text-muted-foreground">At least 6 characters.</p>
          </div>

          {error && (
            <div
              className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2"
              data-testid="register-error"
            >
              {error}
              {/already has an account/i.test(error) && (
                <span className="block mt-1">
                  <Link to="/login" className="underline underline-offset-4">Sign in</Link>
                  {" or "}
                  <Link
                    to={`/forgot-password?email=${encodeURIComponent(form.email.trim())}`}
                    className="underline underline-offset-4"
                    data-testid="register-reset-link"
                  >
                    reset your password
                  </Link>
                </span>
              )}
            </div>
          )}

          {mini && (
            <label className="flex items-center gap-3 text-sm bg-opp/10 border border-opp/30 rounded-lg px-3 py-2.5 cursor-pointer" data-testid="register-keep-mini">
              <input type="checkbox" checked={keepMini} onChange={(e) => setKeepMini(e.target.checked)} className="w-4 h-4 accent-[#4FB0F0]" />
              <span>
                {mini.streak > 1
                  ? `Keep your ${mini.streak}-day puzzle streak`
                  : `Keep your mini puzzle results (${mini.wins} solved)`}
              </span>
            </label>
          )}

          <p className="text-xs text-muted-foreground text-center" data-testid="register-legal">
            By creating an account you agree to our{" "}
            <Link to="/terms" className="text-foreground underline underline-offset-4">Terms</Link> and{" "}
            <Link to="/privacy" className="text-foreground underline underline-offset-4">Privacy policy</Link>.
          </p>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-brand-foreground font-semibold"
            data-testid="register-submit-button"
          >
            {loading ? "Creating account…" : "Create account"}
          </Button>

          <div className="text-center text-sm text-muted-foreground pt-2">
            Already playing?{" "}
            <Link to="/login" className="text-foreground underline-offset-4 hover:underline" data-testid="register-to-login-link">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}
